import React, { Component, MouseEvent } from "react";
import DataGrid, { Column, FilterPanel, FilterRow, GroupPanel, HeaderFilter, Pager, Paging, Scrolling, Search, SearchPanel } from "devextreme-react/data-grid";
import { LoadPanel, Popup, ScrollView } from "devextreme-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import LightModePageTemplate from "../page/Templates/LightModeTemplate";
import RightToWorkChecksService, { RightToWorkCheckRow } from "../../services/RightToWorkChecksService";
import ArtistProfileComponent from "../Artist-Profile/ArtistProfileComponent";
import GridUtils from "../grid/GridUtilities";
import { confirm } from "devextreme/ui/dialog";

interface RightToWorkChecksGridProps {
    rightToWorkCheckStatusId: string;
    serviceTypeId: string;
}

interface RightToWorkChecksGridState {
    dataSource: RightToWorkCheckRow[];
    loadIndicatorForGrid: boolean;
    popupVisible: boolean;
    selectedBusinessEntityId: string;
}

class RightToWorkChecksGrid extends Component<RightToWorkChecksGridProps> {
    state: RightToWorkChecksGridState;
    rightToWorkChecksService: RightToWorkChecksService;
    gridUtils: GridUtils;

    constructor(props: RightToWorkChecksGridProps) {
        super(props);
        this.rightToWorkChecksService = new RightToWorkChecksService();
        this.gridUtils = new GridUtils();
        this.state = {
            dataSource: [],
            loadIndicatorForGrid: false,
            popupVisible: false,
            selectedBusinessEntityId: "",
        };
    }

     
    /**
     * Fetches Right To Work check records from the API and stores them in component state.
     */
    fetchRightToWorkChecks = () => {
        this.rightToWorkChecksService
            .getRightToWorkChecks(this.props.rightToWorkCheckStatusId || undefined)
            .then((response) => {
                const data = response.data?.data ?? response.data ?? [];
                this.setState({
                    dataSource: Array.isArray(data) ? data : [],
                    loadIndicatorForGrid: false,
                });
            })
            .catch((error) => {
                this.setState({ loadIndicatorForGrid: false });
                const message = "RightToWorkChecks data load failed: " + JSON.stringify(error);
                if (!this.rightToWorkChecksService.traceAsErrorToAppInsights(message)) {
                    console.error(message);
                }
            });
    };

    componentDidMount() {
        this.loadDataSource();
    }

    componentDidUpdate(prevProps: RightToWorkChecksGridProps) {
        if (
            prevProps.rightToWorkCheckStatusId !== this.props.rightToWorkCheckStatusId ||
            prevProps.serviceTypeId !== this.props.serviceTypeId
        ) {
            this.loadDataSource();
        }
    }

     
    /**
     * Triggers a data reload with the current filter props.
     */
    loadDataSource = () => {
        this.setState({ loadIndicatorForGrid: true }, this.fetchRightToWorkChecks);
    };

     
    /**
     * Displays provider profile icon column. Clicking opens the Artist Details popup
     * for the row's associated provider.
     */
    profileDisplay = (cellInfo: any) => {
        const businessEntityId: string = cellInfo?.data?.providerId || "";
        return (
            <div className="d-flex justify-content-between align-items-center">
                <button
                    className={"btn icon-btn"}
                    aria-label="View artist details"
                    onClick={(e: MouseEvent) => {
                        e.preventDefault();
                        this.setState({
                            popupVisible: true,
                            selectedBusinessEntityId: String(businessEntityId),
                        });
                    }}
                >
                    <FontAwesomeIcon icon={faUser} />
                </button>
            </div>
        );
    };

    /**
     * Handles the Popup onHiding event. Cancels the native DevExtreme hide and shows
     * a confirm dialog — matching the pattern used in ArtistGrid and SignUpProfileUpdateGrid
     * to prevent the ResizeObserver loop notification.
     */
    hideProfilePopup = (e: any) => {
        e.cancel = true;
        confirm("<div class='text-center'>Close screen without saving?</div>", "Close").then((hasConfirmed) => {
            if (hasConfirmed) {
                this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
            }
        });
    };

    /**
     * Closes the popup directly.
     */
    hideProfileTab = () => {
        this.setState({ popupVisible: false, selectedBusinessEntityId: "" });
    };

    handlePopUpClose = (flag: boolean) => {
        this.setState({
            popupVisible: flag,
            selectedBusinessEntityId: flag ? this.state.selectedBusinessEntityId : "",
        });
    };

    render() {
        return (
            <>
                <LightModePageTemplate>
                    <DataGrid
                        elementAttr={{ id: "gridContainer" }}
                        dataSource={this.state.dataSource}
                        showBorders={false}
                        showColumnLines={false}
                        allowColumnReordering={true}
                        allowColumnResizing={true}
                        columnAutoWidth={true}
                        columnResizingMode={"widget"}
                        hoverStateEnabled={true}
                        noDataText={"No Right To Work checks found for the filters used"}
                        columnMinWidth={120}
                    >
                        <Paging defaultPageSize={10} />
                        <Pager showPageSizeSelector={true} allowedPageSizes={[5, 10, 20]} showInfo={true} />
                        <HeaderFilter visible={true}>
                            <Search enabled={true} />
                        </HeaderFilter>
                        <FilterRow visible={true} applyFilter="auto" />
                        <GroupPanel visible={true} />
                        <FilterPanel visible={true} />
                        <Scrolling useNative={true} showScrollbar={"always"} />
                        <SearchPanel visible={true} placeholder={"Search"} />

                        <Column caption="PROFILE" allowExporting={false} cellRender={this.profileDisplay} />
                        <Column dataField="status" caption="STATUS" />
                        <Column dataField="providerId" caption="PROVIDER ID" />
                        <Column dataField="provider" caption="PROVIDER" />
                        <Column dataField="professionalName" caption="PROFESSIONAL NAME" />
                        <Column dataField="contactName" caption="CONTACT NAME" />
                        <Column dataField="contactNo" caption="CONTACT NO" />
                       
                        <Column
                            dataField="lastUpdatedOn"
                            caption="LAST UPDATED"
                            calculateSortValue={(rowData: RightToWorkCheckRow) => {
                                return this.gridUtils.convertddmmyyyyStringToDate(rowData.lastUpdatedOn);
                            }}
                        />
                        <Column dataField="lastUpdatedBy" caption="UPDATED BY" />
                    </DataGrid>
                    <LoadPanel shadingColor="rgba(0,0,0,0.4)" visible={this.state.loadIndicatorForGrid} />
                </LightModePageTemplate>
                {this.state.popupVisible ? (
                    <Popup
                        visible={this.state.popupVisible}
                        onHiding={this.hideProfilePopup}
                        dragEnabled={false}
                        hideOnOutsideClick={true}
                        showTitle={true}
                        showCloseButton={true}
                        title="Artist Details"
                        resizeEnabled={true}
                    >
                        <ScrollView width="100%" height="100%">
                            
                            <ArtistProfileComponent
                                mode="view"
                                id={this.state.selectedBusinessEntityId}
                                businessEntityId={this.state.selectedBusinessEntityId}
                                handlePopUpClose={this.handlePopUpClose}
                                handleFeedbackUpdate={() => { return null }}
                                handleAddArtist={() => { return null }}
                                serviceType={"Entertainment"}
                            />
                        </ScrollView>
                    </Popup>
                ) : (null)}
            </>
        );
    }
}

export default RightToWorkChecksGrid;
